// Sugalarm — Cycle 456: a hero's picture.
//
// Every surface that shows a hero (the "Who's treating?" tiles, the
// "Maya is treating" chip, the timer card's line, the phone custody card,
// and since cycle 457 the Heroes rows and the picture chooser) renders this
// one component, so the picture and its fallback agree everywhere.
//
// Cycle 457 — the order is PHOTO, then PRESET, then MONOGRAM:
//   * a hero's own photo is served behind auth, so it is fetched with the
//     bearer into an object URL (the cycle-105 MemberAvatar technique), from
//     a small ref-counted cache keyed by url + v + size — every tile showing
//     the same hero fetches once; a new picture has a new `v`, so it is a new
//     key and a stale photo can never be shown;
//   * while the photo is loading, or when it fails to load (a lost object, a
//     dropped connection), the PRESET shows — never an empty circle;
//   * a preset key that is missing, unknown or fails to load renders the
//     initials monogram — never a throw, never a broken-image glyph.
// Decorative by default (`alt=""`): every caller prints the hero's name
// beside it; pass `labelled` where the picture is the only thing naming the
// hero.

import { useEffect, useState } from "react";

import { fetchHeroPhotoObjectUrl } from "../../api/heroAvatar";
import {
  HERO_AVATAR_ALT,
  HERO_AVATAR_TINT,
  heroAvatarSrc,
  heroMonogram,
  isHeroAvatarPreset,
} from "../../lib/heroAvatars";
import type { HeroAvatarPhoto } from "../../lib/types";
import styles from "./HeroAvatar.module.css";

interface Props {
  preset: string | null | undefined;
  name: string | null | undefined;
  /** Circle diameter in design px. */
  size: number;
  /** Give the picture its alt text (when no name is printed beside it). */
  labelled?: boolean;
  /** Cycle 457: the hero's own photo, which wins over the preset. */
  photo?: HeroAvatarPhoto | null;
}

// ── the ref-counted photo cache ──────────────────────────────────────────
interface Entry {
  url: string | null;
  refs: number;
  promise: Promise<string | null>;
}
const cache = new Map<string, Entry>();

function photoKey(photo: HeroAvatarPhoto, px: 128 | 256): string {
  return `${photo.url}|${photo.v}|${px}`;
}

function acquire(photo: HeroAvatarPhoto, px: 128 | 256): Entry {
  const key = photoKey(photo, px);
  let e = cache.get(key);
  if (!e) {
    const entry: Entry = { url: null, refs: 0, promise: Promise.resolve(null) };
    entry.promise = fetchHeroPhotoObjectUrl(photo, px).then((u) => {
      entry.url = u;
      return u;
    });
    cache.set(key, entry);
    e = entry;
  }
  e.refs += 1;
  return e;
}

function release(photo: HeroAvatarPhoto, px: 128 | 256): void {
  const key = photoKey(photo, px);
  const e = cache.get(key);
  if (!e) return;
  e.refs -= 1;
  if (e.refs <= 0) {
    cache.delete(key);
    void e.promise.then((u) => {
      if (u) URL.revokeObjectURL(u);
    });
  }
}

/** The derivative a circle of `size` design px wants (2x for density). */
export function heroPhotoPx(size: number): 128 | 256 {
  return size * 2 > 128 ? 256 : 128;
}

export function HeroAvatar({ preset, name, size, labelled = false, photo }: Props) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const photoUrlKey = photo ? `${photo.url}|${photo.v}` : null;
  const px = heroPhotoPx(size);

  useEffect(() => {
    if (!photo) {
      setPhotoUrl(null);
      return;
    }
    let active = true;
    const e = acquire(photo, px);
    setPhotoUrl(e.url);
    void e.promise.then((u) => {
      if (active) setPhotoUrl(u);
    });
    return () => {
      active = false;
      release(photo, px);
    };
    // The key string carries url + v; the object identity does not matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoUrlKey, px]);

  const label = labelled
    ? `${name ?? "Hero"}'s photo`
    : "";

  if (photo && photoUrl) {
    return (
      <span
        className={styles.root}
        style={{ width: size, height: size }}
        data-hero-avatar="photo"
      >
        <img
          className={styles.photo}
          src={photoUrl}
          width={size}
          height={size}
          alt={label}
          draggable={false}
          onError={() => setPhotoUrl(null)}
        />
      </span>
    );
  }

  const src = heroAvatarSrc(preset, size);
  const showImage = src != null && failedSrc !== src;
  const tint = isHeroAvatarPreset(preset) ? HERO_AVATAR_TINT[preset] : undefined;
  return (
    <span
      className={styles.root}
      style={{ width: size, height: size, background: showImage ? tint : undefined }}
      data-hero-avatar={showImage ? preset : "monogram"}
    >
      {showImage ? (
        <img
          className={styles.img}
          src={src}
          width={size}
          height={size}
          alt={labelled && isHeroAvatarPreset(preset) ? HERO_AVATAR_ALT[preset] : ""}
          draggable={false}
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <span
          className={styles.monogram}
          style={{ fontSize: Math.max(13, Math.round(size * 0.4)) }}
          aria-hidden={!labelled}
          aria-label={labelled ? name ?? undefined : undefined}
        >
          {heroMonogram(name)}
        </span>
      )}
    </span>
  );
}
